import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  model,
  Output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Installer, UsersService } from '@ubie/core';
import { FeedbackService, FormHelper } from '@ubie/shared';
import { AccountRegistrationSliderEvent } from '../../../interfaces/account-registration-slider-event.interface';

@Component({
  selector: 'acl-register-contact-details',
  standalone: false,
  templateUrl: './register-contact-details.component.html',
  styleUrl: './register-contact-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterContactDetailsComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UsersService);
  private readonly feedbackService = inject(FeedbackService);

  readonly slideNo = input<number>(2);
  readonly installer = model.required<Installer>();

  @Output() readonly slideTo =
    new EventEmitter<AccountRegistrationSliderEvent>();

  readonly contactDetailsForm: FormGroup = this.formBuilder.group({
    firstname: new FormControl('', Validators.required),
    lastname: new FormControl('', Validators.required),
    telephone: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
    confirmpassword: new FormControl('', Validators.required),
  });

  // FORM VALIDATION VARS
  // ****************************
  // helper for validation
  formHelper: FormHelper = new FormHelper(this.contactDetailsForm);
  // wether submit button was clicked
  readonly isFormSubmitAttempted = signal(false);

  /**
   * Submit new user form
   */
  submitNewUser(): void {
    this.isFormSubmitAttempted.set(true);

    if (this.contactDetailsForm.valid) {
      this.installer.update((installer) => {
        return {
          ...installer,
          ...this.contactDetailsForm.value,
        };
      });

      const email = this.contactDetailsForm.get('email')?.value;
      const password = this.contactDetailsForm.get('password')?.value;
      const confirmpassword =
        this.contactDetailsForm.get('confirmpassword')?.value;

      if (password === confirmpassword) {
        // check if email is already in use
        this.emailExists(email);
      } else {
        this.feedbackService.present(
          'register_matchPasswords',
          0,
          true,
          'danger',
        );
      }
    }
  }

  /**
   * Verify if there is already a user with this email address
   * @param email email to check
   */
  emailExists(email: string): void {
    this.userService.emailExists(email).subscribe({
      next: (result) => {
        // if the email already exists, show feedback
        if (result.emailExists && result.passwordExists) {
          this.feedbackService.present(
            'register_emailAlready',
            0,
            true,
            'danger',
          );
        } else {
          // go to next step
          // copy the form values over the object values
          this.slideTo.emit({
            slideNo: this.slideNo(),
            user: this.installer(),
          });
        }
      },
      error: () => {
        this.feedbackService.present('register_emailError');
      },
    });
  }

  /**
   * go back to slides
   */
  goBack(): void {
    this.slideTo.emit({ slideNo: 0, user: this.installer() });
  }
}
